// @ts-nocheck
/**
 * ツインキーシステム メインフック
 * 全体の状態管理とフロー制御
 * @module twinKey/hooks/useTwinKey
 */

import { useCallback } from 'react';
import { useTwinKeyContext } from '../context/TwinKeyContext';
import {
  generateDungeon,
  generateDungeonPreview,
  validateKeyPair,
  validateExpedition,
  setCardPool,
} from '../core';

/**
 * ツインキーシステムのメインフック
 * @returns {Object} ツインキー操作API
 */
export function useTwinKey() {
  const {
    state,
    actions,
    playerData,
    updatePlayerData,
    allCards,
    onBack,
    getCardById,
  } = useTwinKeyContext();

  // ============================================================
  // 鍵選択
  // ============================================================

  /**
   * 鍵を選択
   * @param {number} slot - スロット番号（1 or 2）
   * @param {Object} card - カード情報
   */
  const selectKey = useCallback((slot, card) => {
    if (slot === 1) {
      actions.setKey1(card);
    } else {
      actions.setKey2(card);
    }

    // 両方選択されたらプレビュー生成
    const key1 = slot === 1 ? card : state.key1;
    const key2 = slot === 2 ? card : state.key2;

    if (key1 && key2) {
      const preview = generateDungeonPreview(key1, key2);
      actions.setDungeonPreview(preview);
    } else {
      actions.setDungeonPreview(null);
    }

    // playerDataに鍵情報を永続化（リロード対応）
    if (updatePlayerData && card) {
      const keyRef = { cardId: card.id, rarity: card.rarity };
      updatePlayerData((prev) => ({
        ...prev,
        twinKey: {
          ...prev.twinKey,
          [slot === 1 ? 'lastKey1' : 'lastKey2']: keyRef,
        },
      }));
    }
  }, [state.key1, state.key2, actions, updatePlayerData]);

  /**
   * 鍵をクリア
   * @param {number} slot - スロット番号（1, 2, or null for both）
   */
  const clearKey = useCallback((slot = null) => {
    if (slot === 1) {
      actions.setKey1(null);
    } else if (slot === 2) {
      actions.setKey2(null);
    } else {
      actions.clearKeys();
    }
    actions.setDungeonPreview(null);

    // playerDataからも削除（リロード対応）
    if (updatePlayerData) {
      updatePlayerData((prev) => ({
        ...prev,
        twinKey: {
          ...prev.twinKey,
          ...(slot === 1 ? { lastKey1: null } : {}),
          ...(slot === 2 ? { lastKey2: null } : {}),
          ...(slot === null ? { lastKey1: null, lastKey2: null } : {}),
        },
      }));
    }
  }, [actions, updatePlayerData]);

  // ============================================================
  // 探索開始
  // ============================================================

  /**
   * 探索を開始
   * @returns {{ success: boolean, error?: string }}
   */
  const startExpedition = useCallback(() => {
    // バリデーション
    const keyValidation = validateKeyPair(state.key1, state.key2);
    if (!keyValidation.valid) {
      actions.setError(keyValidation.reason);
      return { success: false, error: keyValidation.reason };
    }

    const expeditionValidation = validateExpedition(
      state.key1,
      state.key2,
      state.activeParty
    );
    if (!expeditionValidation.valid) {
      actions.setError(expeditionValidation.reason);
      return { success: false, error: expeditionValidation.reason };
    }

    // ダンジョン開始時にパーティのHPを全回復
    if (state.activeParty && state.activeParty.members) {
      const healedMembers = state.activeParty.members.map(member => ({
        ...member,
        stats: {
          ...member.stats,
          currentHp: member.stats?.maxHp || member.stats?.currentHp || 0,
        },
        isAlive: true,
      }));
      const healedParty = {
        ...state.activeParty,
        members: healedMembers,
        updatedAt: Date.now(),
      };
      actions.setActiveParty(healedParty);
    }

    // カードプールを設定（敵生成用）
    if (allCards && allCards.length > 0) {
      setCardPool(allCards);
    }

    // ダンジョン生成
    const dungeon = generateDungeon(state.key1, state.key2);
    actions.startDungeon(dungeon);

    return { success: true };
  }, [state.key1, state.key2, state.activeParty, actions, allCards]);

  // ============================================================
  // 探索終了
  // ============================================================

  /**
   * 探索を終了し結果を処理
   * @param {Object} result - 探索結果
   */
  const finishExpedition = useCallback((result) => {
    // 報酬付与
    if (result && result.rewards) {
      const { gold, exp } = result.rewards;

      // ゴールド追加
      if (gold > 0 && updatePlayerData) {
        updatePlayerData((prev) => ({
          ...prev,
          twinKey: { ...prev.twinKey, gold: (prev.twinKey?.gold || 0) + gold },
        }));
      }

      // 経験値はパーティに分配（useDungeonで処理）
    }

    // 結果画面へ
    actions.endDungeon(result);
  }, [actions, updatePlayerData]);

  /**
   * 探索結果を履歴に保存
   * @param {Object} result - 探索結果（省略時はstate.expeditionResultを使用）
   */
  const saveExpeditionToHistory = useCallback((result = null) => {
    const expeditionResult = result || state.expeditionResult;
    if (!expeditionResult || !updatePlayerData) return;

    // 履歴に追加するデータを作成
    const historyEntry = {
      id: `exp_${Date.now()}`,
      timestamp: Date.now(),
      dungeonName: expeditionResult.dungeonName || '不明',
      floorReached: expeditionResult.floorReached || 0,
      totalFloors: expeditionResult.totalFloors || 0,
      result: expeditionResult.isVictory ? 'victory' :
              expeditionResult.isRetreated ? 'retreat' : 'defeat',
      rewards: {
        gold: (expeditionResult.rewards?.gold || 0) + (expeditionResult.collectedGold || 0),
        exp: expeditionResult.rewards?.exp || 0,
      },
      stats: expeditionResult.stats || {},
    };

    // playerDataに保存
    updatePlayerData((prev) => {
      const currentHistory = prev.twinKey?.expeditionHistory || [];
      // 最新100件まで保持
      const updatedHistory = [historyEntry, ...currentHistory].slice(0, 100);

      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          expeditionHistory: updatedHistory,
        },
      };
    });
  }, [state.expeditionResult, updatePlayerData]);

  /**
   * ハブに戻る（鍵は保持）
   */
  const returnToHub = useCallback(() => {
    // 鍵の組み合わせを保持したまま戻る
    actions.goToHub();
  }, [actions]);

  /**
   * メニューに戻る
   */
  const returnToMenu = useCallback(() => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  // ============================================================
  // TwinKeyデータ管理
  // ============================================================

  /**
   * TwinKeyデータを取得
   * @returns {Object} TwinKeyデータ
   */
  const getTwinKeyData = useCallback(() => {
    return playerData?.twinKey || {
      parties: [],
      activePartyId: null,
      monsterProgress: {},
      stats: {
        totalRuns: 0,
        totalClears: 0,
        totalDefeats: 0,
        deepestFloor: 0,
        totalGoldEarned: 0,
        totalExpEarned: 0,
      },
    };
  }, [playerData]);

  /**
   * TwinKeyデータを更新
   * @param {Function|Object} updater - 更新関数またはオブジェクト
   */
  const updateTwinKeyData = useCallback((updater) => {
    if (!updatePlayerData) return;

    updatePlayerData((prev) => {
      const currentData = prev.twinKey || getTwinKeyData();
      const newData = typeof updater === 'function'
        ? updater(currentData)
        : { ...currentData, ...updater };

      return {
        ...prev,
        twinKey: newData,
      };
    });
  }, [updatePlayerData, getTwinKeyData]);

  // ============================================================
  // 統計更新
  // ============================================================

  /**
   * 探索統計を更新
   * @param {Object} result - 探索結果
   */
  const updateStats = useCallback((result) => {
    updateTwinKeyData((data) => ({
      ...data,
      stats: {
        ...data.stats,
        totalRuns: data.stats.totalRuns + 1,
        totalClears: data.stats.totalClears + (result.isVictory ? 1 : 0),
        totalDefeats: data.stats.totalDefeats + (result.isDefeat ? 1 : 0),
        deepestFloor: Math.max(data.stats.deepestFloor, result.floorsCleared || 0),
        totalGoldEarned: data.stats.totalGoldEarned + (result.rewards?.gold || 0),
        totalExpEarned: data.stats.totalExpEarned + (result.rewards?.exp || 0),
      },
    }));
  }, [updateTwinKeyData]);

  // ============================================================
  // 画面遷移
  // ============================================================

  /**
   * 鍵選択画面へ遷移
   */
  const goToKeySelection = useCallback(() => {
    actions.setScreen('keySelection');
  }, [actions]);

  /**
   * パーティ編成画面へ遷移
   */
  const goToParty = useCallback(() => {
    actions.setScreen('party');
  }, [actions]);

  // ============================================================
  // 探索可能判定
  // ============================================================

  /**
   * 探索を開始できるかどうか
   */
  const canStartExpedition = Boolean(
    state.key1 &&
    state.key2 &&
    state.activeParty &&
    state.activeParty.members &&
    state.activeParty.members.length > 0
  );

  // ============================================================
  // 返却値
  // ============================================================

  return {
    // 状態
    screen: state.screen,
    key1: state.key1,
    key2: state.key2,
    activeParty: state.activeParty,
    currentDungeon: state.currentDungeon,
    dungeonPreview: state.dungeonPreview,
    currentBattle: state.currentBattle,
    battleResult: state.battleResult,
    expeditionResult: state.expeditionResult,
    isLoading: state.isLoading,
    error: state.error,

    // 画面遷移
    setScreen: actions.setScreen,
    goToHub: actions.goToHub,
    goToKeySelection,
    goToParty,

    // 探索可能判定
    canStartExpedition,

    // 鍵操作
    selectKey,
    clearKey,
    swapKeys: actions.swapKeys,

    // パーティ
    setActiveParty: actions.setActiveParty,

    // 探索
    startExpedition,
    finishExpedition,
    returnToHub,
    returnToMenu,

    // データ
    getTwinKeyData,
    updateTwinKeyData,
    updateStats,
    saveExpeditionToHistory,

    // エラー
    clearError: actions.clearError,

    // 外部データ
    playerData,
    allCards,
    getCardById,
  };
}

export default useTwinKey;
